import React, { useEffect, useState } from 'react';
import { Button, Clickable, Col, Icon, Input, Row, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import { useTranslation } from 'react-i18next';
/*
  Global shout-out preset messages shown on the CTA compose screen (3 random
  per session). The seeded defaults are locked server-side — only presets
  added here can be edited or deleted. Messages may contain the literal
  {teamName} token, replaced with the team's display name on the client.
*/
const CtaPresets = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [presets, setPresets] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newPreset, setNewPreset] = useState({ en: '', ja: '' });
  const [saving, setSaving] = useState(false);
  const [teamPresets, setTeamPresets] = useState([]);

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    try {
      setLoading(true);
      const [res, teamRes] = await Promise.all([
        Api.get('/admin/shoutoutPresets/get'),
        Api.get('/admin/shoutoutPresets/listTeamPresets')
      ]);
      if (res?.data?.presets) applyPresets(res.data.presets);
      if (teamRes?.data?.presets) setTeamPresets(teamRes.data.presets);
    } catch (error) {
      console.error('Error fetching shoutout presets:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyPresets = (list) => {
    setPresets(list);
    setDrafts(
      list.reduce((acc, p) => {
        acc[p._id] = { en: p.en, ja: p.ja || '' };
        return acc;
      }, {})
    );
  };

  const handleDraftChange = (id, key, value) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
  };

  const handleAdd = async () => {
    const en = newPreset.en.trim();
    if (!en) return;

    setSaving(true);
    const res = await Api.post('/admin/shoutoutPresets/add', {
      en,
      ja: newPreset.ja.trim()
    });
    setSaving(false);

    if (res?.data?.presets) {
      applyPresets(res.data.presets);
      setNewPreset({ en: '', ja: '' });
      setShowAdd(false);
    }
  };

  const handleCancelAdd = () => {
    setNewPreset({ en: '', ja: '' });
    setShowAdd(false);
  };

  const handleUpdate = async (id) => {
    const draft = drafts[id];
    if (!draft?.en?.trim()) return;

    setSaving(true);
    const res = await Api.put('/admin/shoutoutPresets/update', {
      presetId: id,
      en: draft.en.trim(),
      ja: draft.ja.trim()
    });
    setSaving(false);

    if (res?.data?.presets) {
      applyPresets(res.data.presets);
      setEditingId(null);
    }
  };

  const handleEditToggle = (id) => {
    if (editingId === id) return handleUpdate(id);

    // entering edit — reset the draft to the saved values so a previously
    // abandoned edit doesn't leak in
    const preset = presets.find((p) => p._id === id);
    if (preset) setDrafts((prev) => ({ ...prev, [id]: { en: preset.en, ja: preset.ja || '' } }));
    setEditingId(id);
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Delete this preset?')) return;

    setSaving(true);
    // Api.delete resolves to `true` on success (no response body passthrough)
    const removed = await Api.delete(`/admin/shoutoutPresets/remove/${id}`);
    setSaving(false);

    if (removed === true) applyPresets(presets.filter((p) => p._id !== id));
  };

  if (loading) {
    return (
      <Col wid="100%" pad="64px 0" centerAll>
        <Spinner2 />
      </Col>
    );
  }

  const defaults = presets.filter((p) => p.isDefault);
  const extras = presets.filter((p) => !p.isDefault);

  return (
    <Col wid="100%" pad="24px" gap="32px">
      <Section title={t('admin.ctaPresets.defaultPresets')}>
        {defaults.map((preset) => (
          <Row key={preset._id} wid="100%" gap="16px" marg="0 0 12px" center pad="0px 8px">
            <Text b2 style={{ flex: 1 }}>
              {preset.en}
            </Text>
            <Text b2 col={theme.GREY_60} style={{ flex: 1 }}>
              {preset.ja || '—'}
            </Text>
            <Text c1 col={theme.GREY_60}>
              🔒 Locked
            </Text>
          </Row>
        ))}
      </Section>

      <Section title={t('admin.ctaPresets.teamPresets')}>
        {teamPresets.length ? (
          teamPresets.map((preset) => (
            <Row key={preset._id} wid="100%" gap="12px" marg="0 0 12px" center>
              <Text b2 bold style={{ flex: 0.5 }}>
                {preset.team?.name || preset.team?.username || '—'}
              </Text>
              <Text b2 style={{ flex: 1 }}>
                {preset.en}
              </Text>
              <Text b2 col={theme.GREY_60} style={{ flex: 1 }}>
                {preset.ja || '—'}
              </Text>
            </Row>
          ))
        ) : (
          <Text center c1 col={theme.GREY_60}>
            {t('admin.ctaPresets.noTeamPresetsYet')}
          </Text>
        )}
      </Section>

      <Section
        title={t('admin.ctaPresets.customPresets')}
        headerRight={
          !showAdd && (
            <Clickable onClick={() => setShowAdd(true)}>
              <Icon type="add" dimensions={20} col={theme.PRIMARY} />
            </Clickable>
          )
        }
      >
        {extras.length ? (
          extras.map((preset) => (
            <Row key={preset._id} wid="100%" gap="12px" marg="0 0 12px" center>
              {editingId === preset._id ? (
                <>
                  <Input
                    wid="100%"
                    emoji
                    value={drafts[preset._id]?.en || ''}
                    placeholder="English message"
                    onChange={(e) => handleDraftChange(preset._id, 'en', e.target.value)}
                  />
                  <Input
                    wid="100%"
                    emoji
                    value={drafts[preset._id]?.ja || ''}
                    placeholder="Japanese message (optional)"
                    onChange={(e) => handleDraftChange(preset._id, 'ja', e.target.value)}
                  />
                </>
              ) : (
                <>
                  <Text b2 style={{ flex: 1 }}>
                    {preset.en}
                  </Text>
                  <Text b2 col={theme.GREY_60} style={{ flex: 1 }}>
                    {preset.ja || '—'}
                  </Text>
                </>
              )}
              {/* check = save while the row is in edit mode */}
              <Clickable onClick={() => !saving && handleEditToggle(preset._id)}>
                <Icon
                  type={editingId === preset._id ? 'check' : 'edit'}
                  dimensions={15}
                  col={theme.PRIMARY}
                />
              </Clickable>
              <Clickable onClick={() => !saving && handleRemove(preset._id)}>
                <Icon type="delete" dimensions={15} col={theme.GREY_60} />
              </Clickable>
            </Row>
          ))
        ) : (
          <Text center c1 col={theme.GREY_60}>
            {t('admin.ctaPresets.noCustomPresetsYet')}
          </Text>
        )}
      </Section>

      {showAdd && (
        <Section
          title={t('admin.ctaPresets.addACustomPreset')}
          headerRight={
            <Button
              text={t('common.cancel')}
              onClick={handleCancelAdd}
              bg={theme.GREY_60}
              wid="80px"
            />
          }
        >
          <Row wid="100%" gap="12px" center>
            <Input
              wid="100%"
              emoji
              value={newPreset.en}
              placeholder={t('admin.ctaPresets.englishMessage')}
              onChange={(e) => setNewPreset((prev) => ({ ...prev, en: e.target.value }))}
            />
            <Input
              wid="100%"
              emoji
              value={newPreset.ja}
              placeholder={t('admin.ctaPresets.japaneseMessage')}
              onChange={(e) => setNewPreset((prev) => ({ ...prev, ja: e.target.value }))}
            />
            <Button
              text={t('common.add')}
              onClick={handleAdd}
              bg={theme.PRIMARY}
              wid="80px"
              loading={saving}
            />
          </Row>
        </Section>
      )}
    </Col>
  );
};

const Section = ({ title, description, headerRight, children }) => (
  <Col
    wid="100%"
    pad="0px 20px 10px 20px"
    style={{
      backgroundColor: theme.GREY_10 || '#f9f9f9',
      borderRadius: '12px',
      border: `1px solid ${theme.GREY_20 || '#eee'}`
    }}
  >
    <Row between center pad="20px 0px">
      <Text h4 bold>
        {title}
      </Text>
      {headerRight || null}
    </Row>
    {description && (
      <Text c1 col={theme.GREY_60} marg="0 0 16px">
        {description}
      </Text>
    )}
    {children}
  </Col>
);

export default CtaPresets;
